import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { APIRoutes } from '@/services/api.routes';
import { MimeTypeIcon, type MimeType } from '@/components/ui/MimeTypeIcon';
import type { VideoID, VideoAttachment } from '@/models';
import { ExpandableText } from '@/components/ui/ExpandableText';

interface VideoAttachmentsProps {
  videoId: VideoID;
}

export const VideoAttachments: React.FC<VideoAttachmentsProps> = ({ videoId }) => {
  const [attachments, setAttachments] = useState<VideoAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAttachments();
  }, [videoId]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await APIRoutes.listVideoAttachments(videoId, {
        sort: 'order',
        sortDirection: 'ASC'
      });
      setAttachments(response.attachments);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError('Fehler beim Laden der Anhänge');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachment: VideoAttachment) => {
    try {
      const blob = await APIRoutes.downloadVideoAttachment(attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('Fehler beim Herunterladen der Datei');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Don't render anything if there are no attachments and we're not loading
  if (!loading && attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faPaperclip} className="w-5 h-5" />
        Anhänge
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Lade Anhänge...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <MimeTypeIcon
                mimeType={attachment.blob.mimeType as MimeType}
                className="w-8 h-8 flex-shrink-0"
              />

              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {attachment.filename}
                </div>
                {attachment.description !== null && attachment.description !== '' && (
                  <ExpandableText
                    text={attachment.description}
                    className="text-sm text-gray-600 dark:text-gray-400"
                  />
                )}
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {formatFileSize(attachment.blob.fileSizeBytes)}
                </div>
              </div>

              <button
                onClick={() => handleDownload(attachment)}
                className="flex-shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                title="Herunterladen"
              >
                <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                <span className="hidden sm:inline">Herunterladen</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
